import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TASK_TYPES, taskMeta, customTypeMeta } from "@/lib/portalConstants";
import { printWeek } from "@/lib/printTemplates";
import { TaskDialog } from "@/components/portal/TaskDialog";
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, isSameMonth, isToday,
} from "date-fns";
import {
  Plus, CheckCircle2, Circle, Pencil, Trash2, Loader2, CalendarDays,
  ChevronLeft, ChevronRight, LayoutList, CalendarRange, Printer, Repeat,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const WEEKDAYS = [
  { short: "S", long: "Sun" },
  { short: "M", long: "Mon" },
  { short: "T", long: "Tue" },
  { short: "W", long: "Wed" },
  { short: "T", long: "Thu" },
  { short: "F", long: "Fri" },
  { short: "S", long: "Sat" },
];

export default function Planner() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskDlg, setTaskDlg] = useState({ open: false, edit: null, date: null });
  const [view, setView] = useState("calendar");
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(format(new Date(), "yyyy-MM-dd"));
  const [dayOpen, setDayOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const allTypes = [...TASK_TYPES, ...customTypes];
  const metaOf = (type) => allTypes.find((x) => x.value === type) || taskMeta("other");

  const load = async () => {
    const [t, c, tt] = await Promise.all([
      api.get("/portal/tasks"),
      api.get("/portal/clients"),
      api.get("/portal/task-types"),
    ]);
    setTasks(t.data);
    setClients(c.data);
    setCustomTypes(tt.data.map(customTypeMeta));
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const refreshTypes = async () => {
    const tt = await api.get("/portal/task-types");
    setCustomTypes(tt.data.map(customTypeMeta));
  };

  const openNew = (date) => setTaskDlg({ open: true, edit: null, date: date || selected });
  const openEdit = (t) => setTaskDlg({ open: true, edit: t, date: null });

  const toggle = async (id) => {
    await api.patch(`/portal/tasks/${id}/toggle`);
    load();
  };
  const remove = async (t, scope) => {
    await api.delete(`/portal/tasks/${t.id}${scope === "future" ? "?scope=future" : ""}`);
    toast.success(scope === "future" ? "This and future visits removed" : "Task deleted");
    load();
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name;

  const grouped = tasks.reduce((acc, t) => {
    (acc[t.date] = acc[t.date] || []).push(t);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort();

  const dayList = (grouped[selected] || [])
    .slice()
    .sort((a, b) => (a.start_time || "99:99").localeCompare(b.start_time || "99:99"));

  const openDay = (dateStr) => {
    setSelected(dateStr);
    setDayOpen(true);
  };

  // ----- calendar math -----
  const gridStart = startOfWeek(startOfMonth(cursor));
  const gridEnd = endOfWeek(endOfMonth(cursor));
  const weeks = [];
  let d = gridStart;
  while (d <= gridEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(d);
      d = addDays(d, 1);
    }
    weeks.push(week);
  }

  const printSelectedWeek = () => {
    const ws = startOfWeek(parseISO(selected));
    const we = addDays(ws, 6);
    const buckets = Array.from({ length: 7 }, (_, i) => {
      const day = addDays(ws, i);
      const ds = format(day, "yyyy-MM-dd");
      return {
        dateStr: ds,
        label: format(day, "EEEE, MMMM d"),
        tasks: (grouped[ds] || [])
          .slice()
          .sort((a, b) => (a.start_time || "99:99").localeCompare(b.start_time || "99:99"))
          .map((t) => {
            const meta = metaOf(t.type);
            return {
              time: t.start_time ? `${t.start_time}${t.end_time ? "–" + t.end_time : ""}` : "",
              title: t.title,
              client: t.client_id ? clientName(t.client_id) || "" : "",
              typeLabel: meta.label,
              dot: meta.hex,
              notes: t.notes || "",
              done: !!t.done,
            };
          }),
      };
    });
    const sameMonth = ws.getMonth() === we.getMonth();
    const weekLabel = sameMonth
      ? `Week of ${format(ws, "MMMM d")} – ${format(we, "d, yyyy")}`
      : `Week of ${format(ws, "MMMM d")} – ${format(we, "MMMM d, yyyy")}`;
    printWeek(weekLabel, buckets);
  };

  const taskRow = (t) => {
    const meta = metaOf(t.type);
    const Icon = meta.icon;
    return (
      <div key={t.id} data-testid={`task-row-${t.id}`} className="flex items-center gap-3 px-4 sm:px-5 py-4 group">
        <button onClick={() => toggle(t.id)} data-testid={`toggle-${t.id}`} className="text-sage-700 shrink-0 p-1 -m-1">
          {t.done ? <CheckCircle2 className="h-6 w-6" /> : <Circle className="h-6 w-6 text-charcoal/30" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className={`${t.done ? "line-through text-charcoal/40" : "text-charcoal"}`}>
            {t.title}
            {t.series_id && (
              <span title="Repeats weekly" className="inline-block ml-1.5 align-[-2px]">
                <Repeat className="h-3.5 w-3.5 text-sage" />
              </span>
            )}
          </p>
          <p className="text-xs text-charcoal-soft">
            {t.start_time && <span>{t.start_time}{t.end_time && `–${t.end_time}`} · </span>}
            {t.client_id && clientName(t.client_id)}
            {t.notes && <span className="italic"> · {t.notes}</span>}
          </p>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full ${meta.color} hidden sm:inline-flex items-center gap-1.5 shrink-0`}>
          <Icon className="h-3.5 w-3.5" />
          {meta.label}
        </span>
        <button onClick={() => openEdit(t)} data-testid={`edit-${t.id}`} className="p-2.5 text-charcoal/40 hover:text-charcoal sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
          <Pencil className="h-4 w-4" />
        </button>
        <button onClick={() => (t.series_id ? setDeleteTarget(t) : remove(t))} data-testid={`delete-${t.id}`} className="p-2.5 text-charcoal/40 hover:text-destructive sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    );
  };

  // Big timeline-style item used inside the day pop-up
  const dayItem = (t) => {
    const meta = metaOf(t.type);
    const Icon = meta.icon;
    return (
      <div key={t.id} data-testid={`day-task-${t.id}`} className={`flex gap-3 sm:gap-4 rounded-2xl bg-white border border-charcoal/5 soft-shadow p-4 ${t.done ? "opacity-60" : ""}`}>
        <div className="w-[74px] shrink-0 text-right pt-0.5">
          {t.start_time ? (
            <>
              <p className="font-serif text-lg text-charcoal leading-tight">{t.start_time}</p>
              {t.end_time && <p className="text-[11px] text-charcoal-soft">to {t.end_time}</p>}
            </>
          ) : (
            <p className="text-[11px] text-charcoal-soft pt-1.5">Any time</p>
          )}
        </div>
        <span className={`w-1.5 self-stretch rounded-full shrink-0 ${meta.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`font-medium ${t.done ? "line-through text-charcoal/50" : "text-charcoal"}`}>
              {t.title}
              {t.series_id && (
                <span title="Repeats weekly" className="inline-block ml-1.5 align-[-2px]">
                  <Repeat className="h-3.5 w-3.5 text-sage" />
                </span>
              )}
            </p>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${meta.color} inline-flex items-center gap-1 shrink-0`}>
              <Icon className="h-3 w-3" />
              <span className="hidden sm:inline">{meta.label}</span>
            </span>
          </div>
          {(t.client_id || t.notes) && (
            <p className="text-xs text-charcoal-soft mt-1">
              {t.client_id && clientName(t.client_id)}
              {t.notes && <span className="italic">{t.client_id ? " · " : ""}{t.notes}</span>}
            </p>
          )}
          <div className="flex items-center gap-1 mt-2.5">
            <button onClick={() => toggle(t.id)} data-testid={`day-toggle-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/10 px-3 py-1.5 text-xs text-charcoal-soft hover:text-sage-700 hover:border-sage/40 transition-colors">
              {t.done ? <CheckCircle2 className="h-3.5 w-3.5 text-sage" /> : <Circle className="h-3.5 w-3.5" />}
              {t.done ? "Done" : "Mark done"}
            </button>
            <button onClick={() => openEdit(t)} data-testid={`day-edit-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/10 px-3 py-1.5 text-xs text-charcoal-soft hover:text-charcoal transition-colors">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button onClick={() => (t.series_id ? setDeleteTarget(t) : remove(t))} data-testid={`day-delete-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/10 px-3 py-1.5 text-xs text-charcoal-soft hover:text-destructive transition-colors">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="planner-page">
      <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-charcoal">Planner</h1>
          <p className="text-charcoal-soft mt-1 text-sm sm:text-base">Tap any day to see it hour by hour.</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <div className="inline-flex rounded-full bg-white border border-charcoal/10 p-1 soft-shadow" data-testid="view-toggle">
            <button
              onClick={() => setView("calendar")}
              data-testid="view-calendar-button"
              className={`inline-flex items-center gap-2 rounded-full px-3.5 sm:px-4 py-2.5 text-sm font-medium transition-colors ${view === "calendar" ? "bg-sage text-white" : "text-charcoal-soft hover:text-charcoal"}`}
            >
              <CalendarRange className="h-4 w-4" /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list-button"
              className={`inline-flex items-center gap-2 rounded-full px-3.5 sm:px-4 py-2.5 text-sm font-medium transition-colors ${view === "list" ? "bg-sage text-white" : "text-charcoal-soft hover:text-charcoal"}`}
            >
              <LayoutList className="h-4 w-4" /> List
            </button>
          </div>
          <button onClick={() => openNew(selected)} data-testid="add-task-button" className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-4 sm:px-5 py-3 text-sm font-medium hover:bg-sage-dark transition-colors flex-1 sm:flex-none justify-center">
            <Plus className="h-4 w-4" /> Add Task
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sage" /></div>
      ) : view === "calendar" ? (
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow overflow-hidden">
          <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-b border-charcoal/5 gap-2">
            <h2 className="font-serif text-xl sm:text-2xl text-charcoal" data-testid="calendar-month-label">{format(cursor, "MMMM yyyy")}</h2>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button onClick={printSelectedWeek} data-testid="print-week-button" title="Print the week of the selected day" className="inline-flex items-center gap-2 rounded-full border border-charcoal/10 px-3.5 sm:px-4 py-2 text-sm text-charcoal-soft hover:text-charcoal hover:border-charcoal/25 transition-colors">
                <Printer className="h-4 w-4" /> <span className="hidden sm:inline">Print week</span>
              </button>
              <button onClick={() => { setCursor(new Date()); setSelected(format(new Date(), "yyyy-MM-dd")); }} data-testid="today-button" className="rounded-full border border-charcoal/10 px-3.5 sm:px-4 py-2 text-sm text-charcoal-soft hover:text-charcoal hover:border-charcoal/25 transition-colors">
                Today
              </button>
              <button onClick={() => setCursor(addMonths(cursor, -1))} data-testid="prev-month-button" className="p-2.5 rounded-full text-charcoal-soft hover:bg-sand transition-colors">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button onClick={() => setCursor(addMonths(cursor, 1))} data-testid="next-month-button" className="p-2.5 rounded-full text-charcoal-soft hover:bg-sand transition-colors">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 px-4 sm:px-5 py-3 border-b border-charcoal/5" data-testid="calendar-legend">
            {allTypes.map((t) => (
              <span key={t.value} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${t.color}`}>
                <t.icon className="h-3 w-3" />
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden">{t.label.split(" ")[0].replace("/", "")}</span>
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 border-b border-charcoal/5">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="py-2 sm:py-2.5 text-center text-[10px] sm:text-xs font-medium uppercase tracking-wider text-charcoal-soft">
                <span className="hidden sm:inline">{w.long}</span>
                <span className="sm:hidden">{w.short}</span>
              </div>
            ))}
          </div>
          <div data-testid="calendar-grid">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 divide-x divide-charcoal/5 border-b border-charcoal/5 last:border-b-0">
                {week.map((dayDate) => {
                  const dateStr = format(dayDate, "yyyy-MM-dd");
                  const dayTasks = grouped[dateStr] || [];
                  const inMonth = isSameMonth(dayDate, cursor);
                  const isSelected = selected === dateStr;
                  return (
                    <button
                      key={dateStr}
                      onClick={() => openDay(dateStr)}
                      data-testid={`calendar-day-${dateStr}`}
                      className={`min-h-[64px] sm:min-h-[88px] md:min-h-[108px] p-1.5 sm:p-2 text-left align-top transition-colors touch-manipulation ${inMonth ? "" : "bg-sand/40"} ${isSelected ? "bg-sage/10 ring-2 ring-inset ring-sage/40" : "active:bg-sand hover:bg-sand/60"}`}
                    >
                      <span className={`inline-flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full text-xs sm:text-sm ${isToday(dayDate) ? "bg-sage text-white font-semibold" : inMonth ? "text-charcoal" : "text-charcoal/30"}`}>
                        {format(dayDate, "d")}
                      </span>
                      {/* colored dots on phones & small tablets */}
                      <span className="flex flex-wrap gap-1 mt-1.5 md:hidden">
                        {dayTasks.slice(0, 5).map((t) => (
                          <span key={t.id} className={`h-2 w-2 rounded-full ${metaOf(t.type).dot}`} />
                        ))}
                        {dayTasks.length > 5 && <span className="text-[10px] text-charcoal-soft leading-none">+{dayTasks.length - 5}</span>}
                      </span>
                      {/* colored chips on larger screens */}
                      <span className="hidden md:flex flex-col gap-1 mt-1">
                        {dayTasks.slice(0, 3).map((t) => {
                          const meta = metaOf(t.type);
                          return (
                            <span
                              key={t.id}
                              data-testid={`calendar-task-${t.id}`}
                              className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] leading-4 ${meta.color} ${t.done ? "opacity-50 line-through" : ""}`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${meta.dot}`} />
                              {t.series_id && <Repeat className="h-2.5 w-2.5 shrink-0 opacity-60" />}
                              {t.start_time && <span className="font-semibold shrink-0">{t.start_time}</span>}
                              <span className="truncate">{t.title}</span>
                            </span>
                          );
                        })}
                        {dayTasks.length > 3 && <span className="text-[11px] text-charcoal-soft pl-1">+{dayTasks.length - 3} more</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : dates.length === 0 ? (
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-14 text-center">
          <CalendarDays className="h-10 w-10 text-sage/50 mx-auto mb-4" />
          <p className="text-charcoal-soft">No tasks yet. Add your first one to start planning.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {dates.map((date) => (
            <div key={date}>
              <h2 className="text-sm font-medium text-charcoal-soft mb-3">{format(parseISO(date), "EEEE, MMMM d, yyyy")}</h2>
              <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow divide-y divide-charcoal/5">
                {grouped[date].map(taskRow)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Day blow-up: tap a calendar day to see it hour by hour */}
      <Dialog open={dayOpen} onOpenChange={setDayOpen}>
        <DialogContent className="bg-sand max-w-lg max-h-[85vh] overflow-y-auto" data-testid="day-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl sm:text-3xl" data-testid="day-dialog-title">
              {selected && format(parseISO(selected), "EEEE, MMMM d")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-charcoal-soft -mt-1" data-testid="day-dialog-count">
            {dayList.length === 0
              ? "Nothing scheduled — enjoy the calm."
              : `${dayList.length} ${dayList.length === 1 ? "thing" : "things"} on the books`}
          </p>
          <div className="space-y-3 mt-2">
            {dayList.map(dayItem)}
          </div>
          {dayList.length === 0 && (
            <div className="bg-white rounded-2xl border border-charcoal/5 p-10 text-center">
              <CalendarDays className="h-9 w-9 text-sage/50 mx-auto mb-3" />
              <p className="text-charcoal-soft text-sm">A wide-open day.</p>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => { setDayOpen(false); openNew(selected); }}
              data-testid="day-add-task"
              className="w-full rounded-full bg-sage text-white py-3.5 font-medium hover:bg-sage-dark transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" /> Add to this day
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TaskDialog
        open={taskDlg.open}
        onOpenChange={(o) => setTaskDlg((s) => ({ ...s, open: o }))}
        clients={clients}
        allTypes={allTypes}
        customTypeCount={customTypes.length}
        editTask={taskDlg.edit}
        initialDate={taskDlg.date}
        onTypesChanged={refreshTypes}
        onSaved={load}
      />

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="bg-sand max-w-sm" data-testid="delete-series-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Repeating weekly visit</DialogTitle>
          </DialogHeader>
          <p className="text-charcoal-soft text-sm">
            “{deleteTarget?.title}” is part of a weekly series. What would you like to remove?
          </p>
          <div className="space-y-2 mt-2">
            <button
              onClick={() => { remove(deleteTarget); setDeleteTarget(null); }}
              data-testid="delete-single-button"
              className="w-full rounded-full border border-charcoal/15 bg-white py-3 text-sm font-medium text-charcoal hover:border-charcoal/30 transition-colors"
            >
              Just this visit
            </button>
            <button
              onClick={async () => { await remove(deleteTarget, "future"); setDeleteTarget(null); }}
              data-testid="delete-series-button"
              className="w-full rounded-full bg-terracotta text-white py-3 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              This and all future visits
            </button>
            <button
              onClick={() => setDeleteTarget(null)}
              data-testid="delete-cancel-button"
              className="w-full rounded-full py-2.5 text-sm text-charcoal-soft hover:text-charcoal transition-colors"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
